import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Mark } from '@skin/Mark'
import { meta } from '@skin/meta'
import './consent.css'

// Where a CLIENT'S IT ADMINISTRATOR lands after granting (or declining) organisation-wide consent.
// They have no Scout account and never will, so this route sits outside RequireSession. It is the only
// screen at this company that a stranger sees, and it is read by someone whose job is to be sceptical:
// so it states exactly what the grant permits, exactly what it cannot reach, and who to call — and it
// never claims a capability the app registration does not actually request.
//
// The permission list below mirrors GraphOAuthClient.Scopes. If that constant changes, change this.
const PERMISSIONS = [
  { scope: 'User.Read', plain: 'Read the signed-in person’s own name and email address.', why: 'To label the connection with the person it belongs to.' },
  { scope: 'Mail.Read', plain: 'Read the signed-in person’s own mailbox — every folder, including sent, archive and deleted items.', why: 'Scout learns how that person writes, and which relationships are warm, from their own correspondence.' },
  { scope: 'Calendars.Read', plain: 'Read the signed-in person’s own calendar.', why: 'Who actually met whom is the strongest relationship signal there is.' },
  { scope: 'offline_access', plain: 'Keep the connection alive without asking the person to sign in every hour.', why: 'Imports run overnight; a nightly job cannot prompt anyone for a password.' },
]

export function Consent() {
  const [params] = useSearchParams()
  const granted = params.get('granted') === '1'
  const error = params.get('error')
  const tenant = params.get('tenant')
  const outcome = useMemo(() => (error ? 'declined' : granted ? 'granted' : 'info'), [error, granted])

  return (
    <div className="radar-stage consent-stage">
      <div className="radar-scanlines" />
      <div className="radar-vignette" />
      <div className="consent-canvas">
        <article className="consent-card">
          <header className="consent-head">
            <div className="brand brand-dark">
              <Mark size={32} />
              <span className="brand-name">{meta.productName}</span>
            </div>
            <p className="brand-sub">by Three Point Turn</p>
          </header>

          {outcome === 'granted' && (
            <div className="consent-verdict ok" role="status">
              <h1>Scout is approved for your organisation.</h1>
              <p>
                Your people can now connect their own mailbox to Scout themselves. Nothing has been read yet —
                each person still has to sign in and approve it for their own account.
              </p>
              {tenant && <p className="consent-tenant">Tenant <code>{tenant}</code></p>}
            </div>
          )}

          {outcome === 'declined' && (
            <div className="consent-verdict bad" role="alert">
              <h1>Consent was not granted.</h1>
              <p>{error}</p>
              <p>
                Nothing was changed in your tenant, and Scout has no access to anything. If this was not what
                you intended, the link that brought you here can be opened again.
              </p>
            </div>
          )}

          {outcome === 'info' && (
            <div className="consent-verdict" role="status">
              <h1>Approving Scout for your organisation</h1>
              <p>
                Scout is a business-development tool used by your firm. It reads a person’s own mail and
                calendar — with that person’s permission — to work out which relationships are warm and how
                that person writes.
              </p>
            </div>
          )}

          <section className="consent-section">
            <h2>What this grant permits</h2>
            <p className="consent-lede">
              Every permission below is <strong>delegated</strong>. That means Scout acts only as the person
              who signed in, only for as long as that person allows it, and can never see anything that person
              could not already open themselves.
            </p>
            <ul className="consent-perms">
              {PERMISSIONS.map((p) => (
                <li key={p.scope}>
                  <code>{p.scope}</code>
                  <span className="perm-plain">{p.plain}</span>
                  <span className="perm-why">{p.why}</span>
                </li>
              ))}
            </ul>
            {/* OneNote is a SEPARATE consent (GraphNotesOAuthClient.Scopes): its own redirect, its own token, never bundled with the grant above. */}
            <p className="consent-lede">
              A person may also connect their OneNote. That is a separate, optional consent of its own — <code>Notes.Read</code>,
              read-only, delegated the same way — and it is never bundled into the mailbox grant above.
            </p>
          </section>

          <section className="consent-section">
            <h2>What it cannot do</h2>
            <ul className="consent-nots">
              <li>Read anyone’s mailbox tenant-wide. Scout holds <em>no</em> application permissions — only per-person, delegated ones.</li>
              <li>Send, reply, move, or delete a message. Every permission it asks for is read-only.</li>
              <li>Read a mailbox belonging to someone who has not personally connected it.</li>
              <li>Keep reading after you revoke it. Removing the enterprise application, or a person disconnecting in Scout, ends the access immediately.</li>
            </ul>
          </section>

          <section className="consent-section">
            <h2>How to revoke it</h2>
            <p>
              In the Microsoft Entra admin centre, open <strong>Enterprise applications</strong>, find
              <strong> Scout</strong>, and delete it. That withdraws the organisation-wide grant and every
              individual grant underneath it in one step.
            </p>
          </section>

          <footer className="consent-foot">
            <div>
              <span>Publisher</span>
              <strong>Three Point Turn, Inc.</strong>
            </div>
            <div>
              <span>Questions</span>
              <strong><a href="mailto:support@threepointturn.com">support@threepointturn.com</a></strong>
            </div>
          </footer>
        </article>
      </div>
    </div>
  )
}
